import { useEffect, useMemo, useState } from "react";
import {
  OPS_PACK_IDS,
  OPS_ROLE_BLURBS,
  OPS_ROLE_OPTIONS,
  OS_PACK_IDS,
  PG_DN_BRAND_VALUE,
  PRESET_BLURBS,
  PRESET_LABELS,
  applyRequiredOpsLevels,
  brandLabel,
  clampOpsLevelChange,
  expandUnitToStoreNames,
  isOpsModuleLocked,
  isPgDnBrandSelection,
  loginKindLabel,
  locationScopeValuesFromGrants,
  matchStore,
  opsRoleFromPreset,
  scopeForOpsRole,
  scopeForPreset,
  storeLabel,
  storesForBrand,
  unitNameFromLocationScopes,
  unitSitesFromStores,
} from "./identityScope";

export function grantsToLevels(grants, catalog) {
  const levels = {
    os: Object.fromEntries((catalog.os || []).map((m) => [m.id, "off"])),
    ops: Object.fromEntries((catalog.ops || []).map((m) => [m.id, "off"])),
  };
  (grants || []).forEach((grant) => {
    const app = grant.app;
    if (app !== "os" && app !== "ops") return;
    if (!(grant.module in levels[app])) return;
    const next = grant.action === "edit" ? "edit" : "view";
    if (levels[app][grant.module] === "edit") return;
    levels[app][grant.module] = next;
  });
  return levels;
}

function emptyLevels(catalog) {
  return {
    os: Object.fromEntries((catalog.os || []).map((m) => [m.id, "off"])),
    ops: Object.fromEntries((catalog.ops || []).map((m) => [m.id, "off"])),
  };
}

function levelsToGrants(levels, { opsScopeType, opsScopeValue, opsScopeValues }) {
  const out = [];
  const locationValues =
    opsScopeType === "location"
      ? (opsScopeValues || []).map((v) => String(v || "").trim()).filter(Boolean)
      : [];
  const values =
    opsScopeType === "location"
      ? locationValues.length
        ? locationValues
        : [String(opsScopeValue || "").trim()].filter(Boolean)
      : opsScopeType === "brand"
        ? [String(opsScopeValue || "").trim()].filter(Boolean)
        : [null];
  Object.entries(levels || {}).forEach(([app, modules]) => {
    Object.entries(modules || {}).forEach(([module, level]) => {
      if (level === "off") return;
      const isOps = app === "ops";
      const scopeType = isOps ? opsScopeType : "all";
      const needsValue = isOps && (scopeType === "location" || scopeType === "brand");
      if (!needsValue) {
        out.push({
          app,
          module,
          action: level,
          scope_type: scopeType,
          scope_value: null,
        });
        return;
      }
      values.forEach((value) => {
        out.push({
          app,
          module,
          action: level,
          scope_type: scopeType,
          scope_value: value || null,
        });
      });
    });
  });
  return out;
}

function presetGrants(catalog, presetId) {
  return (catalog.presets || []).find((p) => p.id === presetId)?.grants || [];
}

function mergeAppLevels(base, fromGrants, app) {
  return {
    ...base,
    [app]: { ...base[app], ...(fromGrants[app] || {}) },
  };
}

const EMPTY_LIST = [];

const fieldStyle = {
  width: "100%",
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--text-primary)",
  borderRadius: 10,
  padding: "8px 10px",
  fontSize: 13,
};

function initialScopeValue(grants) {
  const locs = locationScopeValuesFromGrants(grants);
  if (locs.length) return locs[0];
  const ops = (grants || []).find((g) => g.app === "ops" && g.scope_value);
  return ops?.scope_value ? String(ops.scope_value) : "";
}

function initialScopeValues(grants) {
  return locationScopeValuesFromGrants(grants);
}

function brandIdFromScope(stores, scopeType, scopeValue, scopeValues) {
  if (scopeType === "brand") {
    if (!scopeValue) return "";
    const n = Number(scopeValue);
    return Number.isFinite(n) ? String(n) : "";
  }
  if (scopeType === "location") {
    const locs =
      scopeValues && scopeValues.length
        ? scopeValues
        : scopeValue
          ? [scopeValue]
          : [];
    if (unitNameFromLocationScopes(stores, locs)) return PG_DN_BRAND_VALUE;
    const hit = matchStore(stores, locs[0] || scopeValue);
    return hit?.brand_id != null ? String(hit.brand_id) : "";
  }
  if (!scopeValue) return "";
  const hit = matchStore(stores, scopeValue);
  return hit?.brand_id != null ? String(hit.brand_id) : "";
}

export default function GrantEditor({
  catalog,
  identityKind,
  initialGrants,
  initialPreset,
  initialOpsRole,
  busy,
  submitLabel,
  onCancel,
  onSave,
}) {
  const limited = identityKind === "limited";
  const brands = catalog.brands || EMPTY_LIST;
  const stores = catalog.stores || EMPTY_LIST;
  const catalogPresets = catalog.presets || EMPTY_LIST;

  const startRole = (() => {
    if (initialOpsRole) return String(initialOpsRole);
    if (initialPreset) return opsRoleFromPreset(initialPreset);
    return limited ? "employee" : "";
  })();

  const [opsRole, setOpsRole] = useState(startRole);
  const [osPreset, setOsPreset] = useState(() =>
    !limited && initialPreset && OS_PACK_IDS.has(initialPreset) ? initialPreset : ""
  );
  const [opsPreset, setOpsPreset] = useState(() =>
    initialPreset && OPS_PACK_IDS.has(initialPreset) ? initialPreset : ""
  );
  const [osOverridden, setOsOverridden] = useState(false);
  const [opsOverridden, setOpsOverridden] = useState(false);
  const [levels, setLevels] = useState(() => {
    const base = grantsToLevels(
      initialGrants && initialGrants.length
        ? initialGrants
        : presetGrants(catalog, limited ? "crew" : "hq_sales"),
      catalog.catalog
    );
    return applyRequiredOpsLevels(base, startRole);
  });
  const [opsScopeType, setOpsScopeType] = useState(() => {
    const fromRole = scopeForOpsRole(startRole);
    if (fromRole) return fromRole;
    const ops = (initialGrants || []).find((g) => g.app === "ops");
    return ops?.scope_type || (limited ? "self" : "all");
  });
  const [opsScopeValue, setOpsScopeValue] = useState(() => initialScopeValue(initialGrants));
  const [opsScopeValues, setOpsScopeValues] = useState(() => initialScopeValues(initialGrants));
  const [brandId, setBrandId] = useState(() =>
    brandIdFromScope(
      stores,
      (initialGrants || []).find((g) => g.app === "ops")?.scope_type || "",
      initialScopeValue(initialGrants),
      initialScopeValues(initialGrants)
    )
  );
  const [error, setError] = useState(null);

  function setLocationScopes(nextValues) {
    const unique = [];
    const seen = new Set();
    (nextValues || []).forEach((raw) => {
      const value = String(raw || "").trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      unique.push(value);
    });
    setOpsScopeValues(unique);
    setOpsScopeValue(unique[0] || "");
  }

  useEffect(() => {
    const grants =
      initialGrants && initialGrants.length
        ? initialGrants
        : presetGrants(catalog, limited ? "crew" : "hq_sales");
    setLevels(applyRequiredOpsLevels(grantsToLevels(grants, catalog.catalog), startRole));
  }, [catalog, initialGrants, limited, startRole]);

  useEffect(() => {
    if (!brandId) {
      const inferred = brandIdFromScope(stores, opsScopeType, opsScopeValue, opsScopeValues);
      if (inferred) setBrandId(inferred);
    }
  }, [brandId, opsScopeType, opsScopeValue, opsScopeValues, stores]);

  const roleOptions = useMemo(
    () => OPS_ROLE_OPTIONS.filter((r) => !r.id || (limited ? r.limited_ok : true)),
    [limited]
  );

  const osPackOptions = useMemo(
    () => catalogPresets.filter((p) => OS_PACK_IDS.has(p.id) || p.os_pack),
    [catalogPresets]
  );
  const opsPackOptions = useMemo(
    () =>
      catalogPresets.filter(
        (p) =>
          (OPS_PACK_IDS.has(p.id) || p.ops_pack) &&
          (limited ? p.limited_ok : true)
      ),
    [catalogPresets, limited]
  );

  const pgDnMode = opsScopeType === "location" && isPgDnBrandSelection(brandId);
  const unitSites = useMemo(() => unitSitesFromStores(stores), [stores]);
  const brandStores = useMemo(
    () => (pgDnMode ? [] : storesForBrand(stores, brandId)),
    [brandId, pgDnMode, stores]
  );
  const selectedUnitName = useMemo(
    () => (pgDnMode ? unitNameFromLocationScopes(stores, opsScopeValues) || "" : ""),
    [pgDnMode, opsScopeValues, stores]
  );
  const storeSelectValue = pgDnMode ? selectedUnitName : opsScopeValues[0] || opsScopeValue || "";

  function applyOpsRole(id) {
    setOpsRole(id);
    setError(null);
    const nextType = scopeForOpsRole(id) || (limited ? "self" : "all");
    setOpsScopeType(nextType);
    setLevels((prev) => applyRequiredOpsLevels(prev, id));
    if (nextType === "brand") {
      const fromStore = matchStore(stores, opsScopeValue);
      if (fromStore?.brand_id != null) {
        setBrandId(String(fromStore.brand_id));
        setOpsScopeValue(String(fromStore.brand_id));
      } else if (/^\d+$/.test(String(opsScopeValue).trim())) {
        setBrandId(String(opsScopeValue).trim());
      } else if (!isPgDnBrandSelection(brandId)) {
        setOpsScopeValue(brandId);
      }
    }
    if (nextType === "self" || nextType === "all") {
      setOpsScopeValue("");
      setOpsScopeValues([]);
    }
    if (nextType === "location") {
      const locs = opsScopeValues.length ? opsScopeValues : opsScopeValue ? [opsScopeValue] : [];
      const unit = unitNameFromLocationScopes(stores, locs);
      if (unit) {
        setBrandId(PG_DN_BRAND_VALUE);
        setLocationScopes(expandUnitToStoreNames(stores, unit));
        return;
      }
      const hit = matchStore(stores, locs[0]);
      if (hit?.brand_id != null) {
        setBrandId(String(hit.brand_id));
        setLocationScopes([hit.location_name]);
      } else {
        setOpsScopeValue("");
        setOpsScopeValues([]);
      }
    }
  }

  function applyOsPreset(id) {
    setOsPreset(id);
    setOsOverridden(false);
    setError(null);
    if (id === "__disable__") {
      setOsPreset("__disable__");
      setLevels((prev) => ({
        ...prev,
        os: emptyLevels(catalog.catalog).os,
      }));
      return;
    }
    if (!id) return;
    const from = grantsToLevels(presetGrants(catalog, id), catalog.catalog);
    setLevels((prev) => mergeAppLevels(prev, from, "os"));
  }

  function applyOpsPreset(id) {
    setOpsPreset(id);
    setOpsOverridden(false);
    setError(null);
    if (id === "__disable__") {
      setOpsPreset("__disable__");
      setLevels((prev) => {
        const cleared = { ...prev, ops: emptyLevels(catalog.catalog).ops };
        return applyRequiredOpsLevels(cleared, opsRole);
      });
      return;
    }
    if (!id) return;
    const from = grantsToLevels(presetGrants(catalog, id), catalog.catalog);
    setLevels((prev) =>
      applyRequiredOpsLevels(mergeAppLevels(prev, from, "ops"), opsRole)
    );
    const nextType = scopeForOpsRole(opsRole) || scopeForPreset(id);
    if (nextType) setOpsScopeType(nextType);
  }

  function setLevel(app, module, level) {
    if (app === "ops") {
      const next = clampOpsLevelChange(opsRole, module, level);
      setLevels((prev) => ({
        ...prev,
        ops: { ...prev.ops, [module]: next },
      }));
      setOpsOverridden(true);
      return;
    }
    setLevels((prev) => ({
      ...prev,
      [app]: { ...prev[app], [module]: level },
    }));
    setOsOverridden(true);
  }

  function onPickBrand(nextBrand) {
    setBrandId(nextBrand);
    if (opsScopeType === "brand") {
      setOpsScopeValue(nextBrand);
      return;
    }
    if (opsScopeType === "location") {
      setLocationScopes([]);
    }
  }

  function onPickStore(nextValue) {
    const value = String(nextValue || "").trim();
    if (!value) {
      setLocationScopes([]);
      return;
    }
    if (pgDnMode) {
      const children = expandUnitToStoreNames(stores, value);
      setLocationScopes(children);
      return;
    }
    setLocationScopes([value]);
  }

  function submit() {
    if (opsScopeType === "location" && opsScopeValues.length === 0) {
      setError(pgDnMode ? "Select a UNIT site" : "Select a store");
      return;
    }
    if (opsScopeType === "brand" && !String(opsScopeValue).trim()) {
      setError("Select a brand");
      return;
    }
    const withRequired = applyRequiredOpsLevels(levels, opsRole);
    const source = limited ? { ...withRequired, os: emptyLevels(catalog.catalog).os } : withRequired;
    const grants = levelsToGrants(source, { opsScopeType, opsScopeValue, opsScopeValues });
    if (grants.length === 0 && !opsRole) {
      setError("Turn on at least one module or pick a Role");
      return;
    }
    setError(null);
    let savePreset = "custom";
    if (!osOverridden && !opsOverridden) {
      if (opsPreset && opsPreset !== "__disable__") savePreset = opsPreset;
      else if (osPreset && osPreset !== "__disable__") savePreset = osPreset;
    }
    onSave({
      preset: savePreset,
      ops_role: opsRole || null,
      grants,
      ...(opsScopeType === "location"
        ? {
            scope_value: opsScopeValues[0] || "",
            scope_values: opsScopeValues,
          }
        : {}),
      ...(opsScopeType === "brand" ? { scope_value: String(opsScopeValue).trim() } : {}),
    });
  }

  const showOs = !limited;
  const apps = [
    ...(showOs
      ? [{ key: "os", title: "Stiq OS", hint: "OS is for @stiq.gr logins." }]
      : []),
    {
      key: "ops",
      title: "Stiq Ops",
      hint: limited
        ? "Ops modules for this login. Role locks the required floor."
        : "Ops modules. Role locks the required floor for T1–T3 / HR / Employee.",
    },
  ];

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        background: "var(--surface2)",
        borderRadius: 14,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          border: "1px solid var(--border)",
          background: "var(--surface)",
          borderRadius: 10,
          padding: "8px 10px",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
          {loginKindLabel(identityKind)}
        </div>
        <p style={{ margin: "4px 0 0", fontSize: 12, lineHeight: 1.45, color: "var(--text-muted)" }}>
          {limited
            ? "Pick a Role, then optional Ops permission packs. Required modules stay on while a Role is set."
            : "Pick a Role (Employee / T1–T3 / HR), then OS and Ops permission packs. Required Ops modules stay on while a Role is set."}
        </p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", minWidth: 200 }}>
          Role
          <select
            value={opsRole}
            onChange={(e) => applyOpsRole(e.target.value)}
            style={fieldStyle}
          >
            {roleOptions.map((r) => (
              <option key={r.id || "none"} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        {(opsScopeType === "location" || opsScopeType === "brand") && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", minWidth: 160 }}>
            Brand
            <select
              value={brandId}
              onChange={(e) => onPickBrand(e.target.value)}
              style={fieldStyle}
            >
              <option value="">Select brand</option>
              {brands.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {brandLabel(b)}
                </option>
              ))}
              {opsScopeType === "location" ? (
                <option value={PG_DN_BRAND_VALUE}>PG + DiNAS</option>
              ) : null}
            </select>
          </label>
        )}
        {opsScopeType === "location" && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", minWidth: 220, flex: 1 }}>
            {pgDnMode ? "UNIT site" : "Store"}
            <select
              value={storeSelectValue}
              onChange={(e) => onPickStore(e.target.value)}
              style={fieldStyle}
              disabled={!brandId}
            >
              <option value="">
                {!brandId
                  ? "Select brand first"
                  : pgDnMode
                    ? "Select UNIT…"
                    : "Select store…"}
              </option>
              {pgDnMode
                ? unitSites.map((u) => (
                    <option key={u.parent_location_id} value={u.unit_name}>
                      {u.unit_name}
                      {` (${u.child_names.join(" + ")})`}
                    </option>
                  ))
                : brandStores.map((s) => (
                    <option key={s.location_name} value={s.location_name}>
                      {storeLabel(s)}
                    </option>
                  ))}
            </select>
          </label>
        )}
      </div>
      {opsScopeType === "location" && pgDnMode && opsScopeValues.length > 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          Assigns both stores: {opsScopeValues.join(" · ")}
        </p>
      ) : null}
      {opsRole && OPS_ROLE_BLURBS[opsRole] ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>{OPS_ROLE_BLURBS[opsRole]}</p>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        {showOs ? (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", minWidth: 200 }}>
            OS permission preset
            <select
              value={osOverridden ? "" : osPreset}
              onChange={(e) => applyOsPreset(e.target.value)}
              style={fieldStyle}
            >
              <option value="">{osOverridden ? "Custom modules" : "Select preset"}</option>
              <option value="__disable__">Disable (all Off)</option>
              {osPackOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {PRESET_LABELS[p.id] || p.id}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", minWidth: 200 }}>
          Ops permission preset
          <select
            value={opsOverridden ? "" : opsPreset}
            onChange={(e) => applyOpsPreset(e.target.value)}
            style={fieldStyle}
          >
            <option value="">{opsOverridden ? "Custom modules" : "Select preset"}</option>
            <option value="__disable__">Disable (all Off)</option>
            {opsPackOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {PRESET_LABELS[p.id] || p.id}
              </option>
            ))}
          </select>
        </label>
      </div>
      {(osPreset && PRESET_BLURBS[osPreset]) || (opsPreset && PRESET_BLURBS[opsPreset]) ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          {[osPreset && PRESET_BLURBS[osPreset], opsPreset && PRESET_BLURBS[opsPreset]]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: showOs ? "repeat(auto-fit, minmax(240px, 1fr))" : "1fr" }}>
        {apps.map((app) => (
          <div key={app.key}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
              {app.title}
            </div>
            <p style={{ fontSize: 11, color: "var(--text-muted)", margin: "0 0 8px", minHeight: 32 }}>
              {app.hint}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(catalog.catalog[app.key] || [])
                .filter((mod) => {
                  if (mod.id !== "shift") return true;
                  return limited ? app.key === "ops" : app.key === "os";
                })
                .map((mod) => {
                  const value = levels[app.key]?.[mod.id] || "off";
                  const locked = app.key === "ops" && isOpsModuleLocked(opsRole, mod.id);
                  return (
                    <div
                      key={mod.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        border: "1px solid var(--border)",
                        borderRadius: 10,
                        padding: "8px 10px",
                        background: "var(--surface)",
                      }}
                    >
                      <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                        {mod.label}
                        {locked ? " *" : ""}
                      </span>
                      <select
                        value={value}
                        onChange={(e) => setLevel(app.key, mod.id, e.target.value)}
                        style={{ ...fieldStyle, width: 88, padding: "6px 8px" }}
                      >
                        {!locked ? <option value="off">Off</option> : null}
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                      </select>
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
      {opsRole ? (
        <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)" }}>
          * Required for the selected Role — cannot be set Off.
        </p>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {error ? <p style={{ width: "100%", margin: 0, fontSize: 12, color: "#B45309" }}>{error}</p> : null}
        <button type="button" className="btn-base btn-green btn-small" disabled={busy} onClick={submit}>
          {submitLabel}
        </button>
        {onCancel ? (
          <button type="button" className="btn-base btn-outline btn-small" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  );
}
